import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Sparkles } from "lucide-react";
import OrbitHero from "@/components/OrbitHero";
import Reveal from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import { useWaitlist } from "@/context/WaitlistContext";
import {
  PRODUCTS, DECISION_CARDS, DECISION_FLOW, WHY_NAARI, TECHNOLOGY, ROADMAP, ROADMAP_ICONS, TEAM, GOOGLE_PLAY_URL,
} from "@/data/content";
import { CheckCircle2, Linkedin } from "lucide-react";

const ACCENT = { coral: "#F28C82", mint: "#B8E3D9", gold: "#F5C86B" };

export default function Home() {
  const { openWaitlist } = useWaitlist();

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden pt-36 pb-12 md:pt-44 md:pb-20">
        <div className="mesh-bg">
          <div className="mesh-blob" style={{ width: 500, height: 500, background: "#F28C82", top: -120, right: 80 }} />
          <div className="mesh-blob" style={{ width: 460, height: 460, background: "#B8E3D9", top: 100, left: -120 }} />
          <div className="mesh-blob" style={{ width: 380, height: 380, background: "#F5C86B", bottom: -100, right: -60 }} />
        </div>

        <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:px-12 lg:grid-cols-2">
          <div>
            <motion.a
              href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer"
              data-testid="hero-play-badge"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#4B2E5A]/10 bg-white/60 px-4 py-1.5 backdrop-blur-md transition-colors hover:bg-white"
            >
              <Sparkles className="h-4 w-4 text-[#F28C82]" strokeWidth={1.5} />
              <span className="text-xs font-medium text-[#4B2E5A]/70">CookSorted is now live on Google Play</span>
            </motion.a>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 font-heading text-5xl font-light leading-[1.05] tracking-tighter text-[#4B2E5A] md:text-6xl lg:text-7xl"
            >
              Simplifying Everyday <span className="text-[#F28C82]">Decisions</span> with AI
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-[#4B2E5A]/70"
            >
              AI-powered products helping millions decide what to cook, wear, care for, organize, and buy.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Link
                to="/products"
                data-testid="hero-explore-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#4B2E5A] px-8 py-4 text-sm font-medium text-[#FCFAF7] shadow-[0_8px_30px_rgba(75,46,90,0.15)] transition-all hover:-translate-y-0.5 hover:bg-[#4B2E5A]/90"
              >
                Explore Products <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                data-testid="hero-early-access-btn"
                onClick={() => openWaitlist("General")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#4B2E5A]/10 bg-white/80 px-8 py-4 text-sm font-medium text-[#4B2E5A] backdrop-blur-md transition-all hover:bg-white"
              >
                Join Early Access
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }}
            className="flex justify-center lg:justify-end"
          >
            <OrbitHero />
          </motion.div>
        </div>
      </section>

      {/* ---------------- MISSION / DECISIONS ---------------- */}
      <section data-testid="mission-section" className="relative py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Every single day</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A] md:text-5xl">
              Daily life is full of small decisions
            </h2>
            <p className="mt-5 text-lg text-[#4B2E5A]/60">Every day we ask ourselves…</p>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DECISION_CARDS.map((c, i) => {
              const prod = PRODUCTS.find((p) => p.name === c.product);
              const available = prod?.available;
              const cardClass = "glass group flex w-full items-start gap-4 rounded-[24px] p-7 text-left transition-transform hover:-translate-y-1";
              const cardInner = (
                <>
                  <span className="text-3xl">{c.emoji}</span>
                  <div className="flex-1">
                    <p className="font-heading text-lg font-medium text-[#4B2E5A]">{c.q}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm text-[#F28C82] opacity-0 transition-opacity group-hover:opacity-100">
                      {available ? "Download the app" : "Experience it"} <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </>
              );
              return (
                <Reveal key={c.q} delay={i * 0.06}>
                  {available ? (
                    <a href={prod.appUrl} target="_blank" rel="noopener noreferrer" data-testid={`decision-card-${i}`} className={cardClass}>
                      {cardInner}
                    </a>
                  ) : (
                    <button data-testid={`decision-card-${i}`} onClick={() => openWaitlist(c.product)} className={cardClass}>
                      {cardInner}
                    </button>
                  )}
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1} className="mx-auto mt-10 max-w-2xl text-center">
            <p className="text-lg text-[#4B2E5A]/70 md:text-xl">
              At Naari, we're building AI that answers these questions <span className="font-medium text-[#4B2E5A]">intelligently</span>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------------- PRODUCTS ---------------- */}
      <section data-testid="products-section" className="relative py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Our products</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A] md:text-5xl">
              A product for every daily decision
            </h2>
          </Reveal>

          <div className="mt-10 grid gap-6 md:grid-cols-2 md:gap-8">
            {PRODUCTS.map((p, i) => {
              const color = ACCENT[p.accent];
              return (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <div className="glass group relative overflow-hidden rounded-[24px] p-8 transition-transform hover:-translate-y-1 md:p-10">
                    <div className="mb-6 flex items-center gap-4">
                      {p.iconImage ? (
                        <img src={p.iconImage} alt={`${p.name} app icon`} className="h-14 w-14 rounded-2xl object-cover" />
                      ) : (
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: `${color}22` }}>
                          <p.icon className="h-7 w-7" strokeWidth={1.5} style={{ color }} />
                        </div>
                      )}
                      <div>
                        <h3 className="font-heading text-2xl font-medium text-[#4B2E5A]">{p.name}</h3>
                        <span className={`text-xs font-medium uppercase tracking-wider ${p.available ? "text-[#F28C82]" : "text-[#4B2E5A]/40"}`}>{p.available ? "Available Now" : "Coming Soon"}</span>
                      </div>
                    </div>
                    <p className="text-lg leading-relaxed text-[#4B2E5A]/70">{p.tagline}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link
                        to={`/products/${p.slug}`}
                        data-testid={`product-learn-more-${p.slug}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#4B2E5A]/10 bg-white/70 px-6 py-3 text-sm font-medium text-[#4B2E5A] transition-all hover:bg-white"
                      >
                        Learn More <ArrowRight className="h-4 w-4" />
                      </Link>
                      {p.available ? (
                        <GooglePlayBadge height={48} className="self-center" url={p.appUrl} label={p.name} />
                      ) : (
                        <button
                          data-testid={`product-waitlist-${p.slug}`}
                          onClick={() => openWaitlist(p.name)}
                          className="rounded-full bg-[#4B2E5A] px-6 py-3 text-sm font-medium text-[#FCFAF7] transition-all hover:bg-[#4B2E5A]/90"
                        >
                          Join Waitlist
                        </button>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- DECISION INTELLIGENCE ---------------- */}
      <section data-testid="philosophy-section" className="relative overflow-hidden py-12 md:py-20">
        <div className="mesh-bg">
          <div className="mesh-blob" style={{ width: 420, height: 420, background: "#B8E3D9", top: 40, left: -80 }} />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Our philosophy</p>
              <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A] md:text-5xl">
                Powered by Decision Intelligence
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-[#4B2E5A]/70">
                Instead of making you search… we help you decide. Information is everywhere. Good decisions aren't.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-[#4B2E5A]/70">
                We believe AI should reduce cognitive load — not add to it. Naari understands your routines, preferences, lifestyle and context to recommend the best next decision.
              </p>
              <div className="mt-8 space-y-2">
                {["Less searching.", "Less overthinking.", "More living."].map((t) => (
                  <p key={t} className="font-heading text-xl font-medium text-[#4B2E5A]">{t}</p>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="glass rounded-[24px] p-8 md:p-10">
                {DECISION_FLOW.map((step, i) => (
                  <div key={step}>
                    <div className="flex items-center gap-3 rounded-2xl bg-white/60 px-5 py-3.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#4B2E5A] text-xs font-medium text-white">{i + 1}</span>
                      <span className="font-medium text-[#4B2E5A]">{step}</span>
                    </div>
                    <div className="flex justify-center py-1.5"><ArrowDown className="h-4 w-4 text-[#4B2E5A]/30" /></div>
                  </div>
                ))}
                <div className="rounded-2xl bg-[#4B2E5A] px-5 py-4 text-center">
                  <span className="font-heading text-lg font-medium text-[#FCFAF7]">Today's Best Decision</span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- WHY NAARI ---------------- */}
      <section data-testid="why-section" className="relative py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Why Naari</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A] md:text-5xl">
              Built to be lived with
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3 md:gap-8">
            {WHY_NAARI.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div className="glass h-full rounded-[24px] p-8 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#B8E3D9]/40">
                    <w.icon className="h-7 w-7 text-[#4B2E5A]" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-heading text-2xl font-medium text-[#4B2E5A]">{w.title}</h3>
                  <p className="mt-3 leading-relaxed text-[#4B2E5A]/60">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TECHNOLOGY ---------------- */}
      <section data-testid="technology-section" className="relative py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Technology</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A] md:text-5xl">
              The intelligence underneath
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6">
            {TECHNOLOGY.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06}>
                <div className="glass flex items-center gap-4 rounded-[24px] p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F5C86B]/25">
                    <t.icon className="h-6 w-6 text-[#4B2E5A]" strokeWidth={1.5} />
                  </div>
                  <span className="font-medium text-[#4B2E5A]">{t.title}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- ROADMAP ---------------- */}
      <section data-testid="roadmap-section" className="relative py-12 md:py-20">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Roadmap</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A] md:text-5xl">
              Where we're heading
            </h2>
          </Reveal>
          <div className="relative mt-12">
            {/* Timeline axis: left on mobile, perfectly centered on desktop */}
            <div className="absolute left-[27px] top-2 bottom-2 w-px bg-[#4B2E5A]/12 md:left-1/2 md:-translate-x-1/2" />
            {ROADMAP.map((r, i) => (
              <Reveal key={r.year} delay={i * 0.1}>
                <div className="relative mb-10 md:mb-12">
                  {/* Year circle: all centered on the same axis so 2026 / 2027 / Future align */}
                  <div className="absolute left-0 top-0 z-10 flex h-14 w-14 items-center justify-center rounded-full bg-[#4B2E5A] font-heading text-sm font-semibold text-white shadow-lg md:left-1/2 md:-translate-x-1/2">
                    {r.year}
                  </div>
                  {/* Card: offset past the circle on mobile; left/right half on desktop */}
                  <div className={`ml-20 md:ml-0 md:w-1/2 ${i % 2 ? "md:ml-auto md:pl-12" : "md:mr-auto md:pr-12"}`}>
                    <div className="glass rounded-[24px] p-6 text-left">
                      <div className="flex flex-wrap gap-2">
                        {r.items.map((item) => {
                          const Ico = ROADMAP_ICONS[item];
                          return (
                            <span key={item} className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-4 py-1.5 text-sm font-medium text-[#4B2E5A]">
                              {Ico && <Ico className="h-4 w-4 text-[#F28C82]" strokeWidth={1.5} />}
                              {item}
                            </span>
                          );
                        })}
                      </div>
                      <p className="mt-3 text-sm text-[#4B2E5A]/60">{r.note}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TEAM ---------------- */}
      <section data-testid="founder-section" className="relative py-12 md:py-20">
        <div className="mx-auto max-w-6xl space-y-6 px-6 md:space-y-8 md:px-12">
          {TEAM.map((person, idx) => (
            <div key={person.name} className="glass grid items-center gap-10 rounded-[24px] p-8 md:grid-cols-2 md:p-14">
              <Reveal className={idx % 2 ? "md:order-2" : ""}>
                <div className="relative mx-auto h-72 w-72 overflow-hidden rounded-[24px] shadow-[0_20px_60px_rgba(75,46,90,0.15)]">
                  <img src={person.photo} alt={person.label} className="h-full w-full object-cover" />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">{person.label}</p>
                <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A] md:text-4xl">
                  {person.name}
                </h2>
                <div className="mt-6 space-y-3">
                  {person.bio.map((line) => (
                    <div key={line} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-[#B8E3D9]" />
                      <span className="text-lg text-[#4B2E5A]/75">{line}</span>
                    </div>
                  ))}
                </div>
                <a
                  href={person.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${idx === 0 ? "founder" : "cofounder"}-linkedin`}
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#4B2E5A]/10 bg-white/70 px-6 py-3 text-sm font-medium text-[#4B2E5A] transition-all hover:bg-white"
                >
                  <Linkedin className="h-4 w-4" strokeWidth={1.5} /> Connect on LinkedIn
                </a>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
